import { ReactNode, useEffect, useState } from "react";
import { FileDown, Send } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { DestinatarioProposto, EsitoInvio, MAX_DESTINATARI } from "../../api/report";
import { Button } from "./Button";
import { Input } from "./Input";
import { Modal } from "./Modal";

interface InviaReportModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Campi che riguardano il documento e non i destinatari — oggi il periodo del
   * report cliente. Stanno sopra gli indirizzi perché vengono prima anche nel
   * ragionamento: si decide che cosa si manda, poi a chi.
   */
  children?: ReactNode;
  /**
   * Se presente, aggiunge "Scarica PDF" accanto a Invia. Sui report serve
   * davvero: sono documenti che spesso si guardano prima di spedirli, e senza
   * questo bottone bisognerebbe chiudere il pannello, scaricare da un'altra
   * parte e riaprirlo per mandare esattamente lo stesso file.
   */
  scarica?: () => Promise<void>;
  /** Titolo del pannello, es. "Invia il sopralluogo". */
  titolo: string;
  /** Nome del file che verrà allegato, mostrato prima di spedire. */
  nomeAllegato: string;
  /** Carica gli indirizzi proponibili. Chiamata all'apertura, non prima. */
  caricaDestinatari: () => Promise<DestinatarioProposto[]>;
  invia: (destinatari: string[], nota: string) => Promise<EsitoInvio>;
  /**
   * Che cosa dire quando non c'e' nessun indirizzo da proporre. Il testo
   * predefinito parla del «record», che e' giusto per i documenti che partono da
   * una riga dell'anagrafica — ma il Report Ordini aggrega quattro tabelle e non
   * ha nessun record da cui ricavare un indirizzo: li' quella frase manderebbe a
   * cercare in anagrafica una cosa che non c'e'.
   */
  messaggioSenzaProposte?: string;
}

/**
 * Pannello di invio di un documento per email — condiviso da tutti i moduli che
 * ne hanno uno, invece di ricostruire lo stesso modulo in ognuno.
 *
 * ## Due scelte che sembrano dettagli e non lo sono
 *
 * **Nessun indirizzo è spuntato di default.** Mandare il documento di un cliente
 * alla casella sbagliata non si annulla, e una casella già spuntata la si
 * conferma senza leggerla. Il pulsante dice sempre a quanti si sta per mandare.
 *
 * **Il campo libero non è un ripiego.** In anagrafica ha una email meno di metà
 * dei clienti (238 su 480) e 170 sedi su 229: per parecchi record l'app non ha
 * niente da proporre, e questo pannello deve funzionare bene proprio lì.
 */
export function InviaReportModal({
  open,
  onClose,
  titolo,
  nomeAllegato,
  caricaDestinatari,
  invia,
  children,
  scarica,
  messaggioSenzaProposte = "Per questo record l'anagrafica non ha nessun indirizzo. Scrivilo qui sotto.",
}: InviaReportModalProps) {
  const { showToast } = useToast();
  const [proposti, setProposti] = useState<DestinatarioProposto[]>([]);
  const [scelti, setScelti] = useState<string[]>([]);
  const [libero, setLibero] = useState("");
  const [nota, setNota] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Gli indirizzi si caricano all'APERTURA, non al montaggio: il pannello vive
  // dentro una scheda che sta già a schermo, e caricarli prima vorrebbe dire una
  // chiamata per ogni record aperto anche quando nessuno invia niente.
  useEffect(() => {
    if (!open) return;
    setScelti([]);
    setLibero("");
    setNota("");
    setIsLoading(true);
    caricaDestinatari()
      .then(setProposti)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile leggere gli indirizzi."), "error"))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Gli indirizzi che partiranno: le caselle spuntate più, se valorizzato, il
   * campo libero. Un indirizzo digitato a mano che coincide con uno spuntato
   * non conta due volte — riceverebbe due copie della stessa email.
   */
  const destinatari = Array.from(
    new Set([...scelti, ...(libero.trim() ? [libero.trim()] : [])].map((e) => e.trim().toLowerCase()))
  );
  const troppi = destinatari.length > MAX_DESTINATARI;

  function toggle(email: string) {
    setScelti((correnti) =>
      correnti.includes(email) ? correnti.filter((e) => e !== email) : [...correnti, email]
    );
  }

  async function handleInvia() {
    setIsSending(true);
    try {
      const esito = await invia(destinatari, nota);

      // L'esito è per destinatario e va riferito così: "inviato" quando due su
      // tre hanno ricevuto sarebbe falso, ed è un'informazione che non si
      // recupera più — l'email o è partita o non è partita.
      if (esito.falliti.length === 0) {
        showToast(
          esito.inviati.length === 1
            ? `Inviato a ${esito.inviati[0]}.`
            : `Inviato a ${esito.inviati.length} destinatari.`,
          "success"
        );
        onClose();
      } else {
        showToast(
          `${esito.inviati.length} inviati, ${esito.falliti.length} non riusciti: ` +
            esito.falliti.map((f) => `${f.email} (${f.errore})`).join(" · "),
          "warning",
          // Resta a schermo: dice quali indirizzi vanno ritentati, e sono
          // esattamente i dati che servono per rifarlo.
          { persistent: true }
        );
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Invio non riuscito."), "error");
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titolo}
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          {scarica && (
            <Button
              type="button"
              variant="secondary"
              loading={isDownloading}
              onClick={async () => {
                setIsDownloading(true);
                try {
                  await scarica();
                } catch (err) {
                  showToast(getErrorMessage(err, "Impossibile generare il PDF."), "error");
                } finally {
                  setIsDownloading(false);
                }
              }}
            >
              <FileDown className="h-4 w-4" aria-hidden="true" />
              Scarica PDF
            </Button>
          )}
          <Button
            type="button"
            loading={isSending}
            disabled={destinatari.length === 0 || troppi}
            onClick={handleInvia}
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {destinatari.length === 0
              ? "Invia"
              : destinatari.length === 1
                ? "Invia a 1 destinatario"
                : `Invia a ${destinatari.length} destinatari`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {children}

        <p className="text-xs text-ink/60">
          Verrà allegato <strong>{nomeAllegato}</strong>, rigenerato adesso dai dati correnti. L'email parte
          dalla casella di assistenza, quindi le risposte arriveranno lì.
        </p>

        {isLoading ? (
          <p className="text-sm text-ink/40">Caricamento indirizzi…</p>
        ) : proposti.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-ink">Indirizzi dall'anagrafica</p>
            {proposti.map((d) => (
              <label
                key={d.email}
                className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-black/20"
                  checked={scelti.includes(d.email)}
                  onChange={() => toggle(d.email)}
                />
                <span>
                  <span className="block text-ink">{d.etichetta}</span>
                  <span className="block text-xs text-ink/50">{d.email}</span>
                </span>
              </label>
            ))}
          </div>
        ) : (
          /* Non è un errore e non deve sembrarlo: per metà dei clienti in
             anagrafica non c'è nessuna email, e il campo qui sotto è la strada
             normale, non un ripiego. */
          <p className="rounded-lg bg-primary/5 px-3 py-2 text-xs text-ink/70">{messaggioSenzaProposte}</p>
        )}

        <Input
          label="Altro indirizzo"
          type="email"
          placeholder="nome@azienda.it"
          value={libero}
          onChange={(e) => setLibero(e.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Nota per il destinatario (facoltativa)</label>
          <textarea
            rows={3}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
        </div>

        {troppi && (
          <p className="text-xs font-semibold text-accent">
            Massimo {MAX_DESTINATARI} destinatari per invio: adesso sono {destinatari.length}.
          </p>
        )}
      </div>
    </Modal>
  );
}
